import { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';

import { ProjectType } from '@cartwhl/common/types';

import { Header } from 'components/StudioLayout';
import { projectStore } from 'stores';
import { LEGACY_PROJECT_ID } from 'stores/projectStore';

export default function LandingPage() {
  const { allProjects } = useSnapshot(projectStore.state);
  const hasNavigated = useRef(false);

  useEffect(() => {
    // Start loading projects on mount
    if (allProjects.status === 'initial') {
      projectStore.loadProjects();
    }
  }, [allProjects.status]);

  useEffect(() => {
    // Once projects are loaded, find first motion project or create one
    const handleNavigation = async () => {
      if (hasNavigated.current) return;
      if (allProjects.status !== 'ready') return;

      hasNavigated.current = true;

      // Find the first motion project
      const firstMotionProject = allProjects.data.items.find(
        (project) =>
          project.type === ProjectType.MOTION &&
          project.projectID !== LEGACY_PROJECT_ID,
      );

      if (firstMotionProject) {
        // Navigate to existing motion project
        window.location.href = `/motion/${firstMotionProject.projectID}`;
      } else {
        // Create a new motion project
        const newProject = await projectStore.createNewProject(
          ProjectType.MOTION,
        );
        if (newProject) {
          window.location.href = `/motion/${newProject.projectID}`;
        }
      }
    };

    handleNavigation();
  }, [allProjects.status, allProjects.data]);

  return (
    <div className="bg-primary flex h-screen w-screen flex-col">
      {/* Header placeholder */}
      <Header />

      <div className="flex flex-1 flex-col overflow-hidden pb-5 sm:px-5">
        {/* Main content area with border and rounded corners */}
        <div className="border-gray-30 dark:border-gray-75 relative flex flex-1 flex-col overflow-hidden rounded-3xl border">
          {/* Main viewport area */}
          <div className="bg-secondary flex flex-1 items-center justify-center">
            {/* Character placeholder */}
            {/* <div className="bg-gray-30 dark:bg-gray-70 h-96 w-64 animate-pulse rounded-lg" /> */}
          </div>

          {/* Three floating bottom components */}
          <div className="absolute right-4 bottom-4 left-4 flex items-end justify-center sm:justify-between">
            {/* Bottom left component - 117px wide × 264px tall with 6 line items */}
            <div className="border-gray-30 dark:border-gray-75 bg-primary hidden h-[264px] w-[117px] flex-col gap-2 rounded-xl border p-3 sm:flex">
              <div className="bg-gray-30 dark:bg-gray-70 h-8 w-full animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 w-full animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 w-full animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 w-full animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 w-full animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 w-full animate-pulse rounded" />
            </div>

            {/* Bottom middle component - 600px wide × 56px tall with three buttons */}
            <div className="border-gray-30 dark:border-gray-75 bg-primary flex h-14 w-3/4 items-center gap-2 rounded-xl border px-3 sm:w-[600px]">
              {/* On mobile we only show 1 by default */}
              <div className="bg-gray-30 dark:bg-gray-70 h-8 flex-1 animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 hidden h-8 flex-1 animate-pulse rounded sm:block" />
              <div className="bg-gray-30 dark:bg-gray-70 hidden h-8 flex-1 animate-pulse rounded sm:block" />
            </div>

            {/* Bottom right component Desktop - 143px wide × 295px tall with 4 components, first is 100×100 */}
            <div className="border-gray-30 dark:border-gray-75 bg-primary hidden h-[295px] w-[143px] flex-col gap-3 rounded-xl border p-3 sm:flex">
              <div className="bg-gray-30 dark:bg-gray-70 h-[120px] animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-8 animate-pulse rounded" />
            </div>
          </div>

          {/* Bottom right component Mobile - Absolute positioned to stay on right */}
          <div className="border-gray-30 dark:border-gray-75 bg-primary absolute right-4 bottom-[48px] mb-[80px] flex h-[185px] w-[71px] flex-col gap-3 rounded-xl border p-3 sm:hidden">
            <div className="bg-gray-30 dark:bg-gray-70 h-[50px] w-[50px] animate-pulse rounded" />
            <div className="flex w-full flex-col items-end gap-3">
              <div className="bg-gray-30 dark:bg-gray-70 h-6 w-1/2 animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-6 w-1/2 animate-pulse rounded" />
              <div className="bg-gray-30 dark:bg-gray-70 h-6 w-1/2 animate-pulse rounded" />
            </div>
          </div>
        </div>

        {/* Bottom footer section with button */}
        <div className="flex h-12 items-center justify-end">
          <div className="bg-gray-30 dark:bg-gray-70 mt-4 hidden h-[30px] w-[68px] animate-pulse rounded sm:block" />
        </div>
      </div>
    </div>
  );
}
